import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
  PieChart, Pie, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Users, BarChart3, PieChart as PieChartIcon, Download } from 'lucide-react';
import axios from 'axios';

const COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#6366f1', '#14b8a6', '#f97316', '#84cc16'];

// Normalize nationality adjectives / spelling variants to a canonical country name
// so e.g. "India" and "Indian" don't appear as two separate bars.
const NATIONALITY_ALIASES = {
  indian: 'India',
  india: 'India',
  pakistani: 'Pakistan',
  pakistan: 'Pakistan',
  bangladeshi: 'Bangladesh',
  bangladesh: 'Bangladesh',
  nepalese: 'Nepal',
  nepali: 'Nepal',
  nepal: 'Nepal',
  filipino: 'Philippines',
  filipina: 'Philippines',
  philipino: 'Philippines',
  philippine: 'Philippines',
  philippines: 'Philippines',
  phillipines: 'Philippines',
  srilankan: 'Sri Lanka',
  'sri lankan': 'Sri Lanka',
  srilanka: 'Sri Lanka',
  'sri lanka': 'Sri Lanka',
  burmese: 'Myanmar',
  myanmar: 'Myanmar',
  american: 'United States',
  usa: 'United States',
  us: 'United States',
  'united states': 'United States',
  kenyan: 'Kenya',
  kenya: 'Kenya',
  egyptian: 'Egypt',
  egypt: 'Egypt',
  iranian: 'Iran',
  iran: 'Iran',
  moroccan: 'Morocco',
  morocco: 'Morocco',
  cameroonian: 'Cameroon',
  cameroon: 'Cameroon',
  nigerian: 'Nigeria',
  nigeria: 'Nigeria',
  'sierra leonean': 'Sierra Leone',
  'sierra leone': 'Sierra Leone',
  ghanaian: 'Ghana',
  ghana: 'Ghana',
  venezuelan: 'Venezuela',
  venezuela: 'Venezuela',
  serbian: 'Serbia',
  serbia: 'Serbia',
  ugandan: 'Uganda',
  uganda: 'Uganda',
  ethiopian: 'Ethiopia',
  ethiopia: 'Ethiopia',
  british: 'United Kingdom',
  uk: 'United Kingdom',
  'united kingdom': 'United Kingdom',
};

const canonicalizeNationality = (raw) => {
  if (!raw) return 'Unknown';
  const key = String(raw).trim().toLowerCase().replace(/\s+/g, ' ');
  return NATIONALITY_ALIASES[key] || String(raw).trim();
};

const prepareChartData = (obj) => {
  return Object.entries(obj || {}).map(([name, value]) => ({ name, value }));
};

// For nationality: merge aliases then sort by count desc so the largest groups appear at the top.
const prepareNationalityData = (obj) => {
  const merged = {};
  for (const [raw, val] of Object.entries(obj || {})) {
    const key = canonicalizeNationality(raw);
    merged[key] = (merged[key] || 0) + (val || 0);
  }
  return Object.entries(merged)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
};

// ─── Click-to-drill-down: fetch the member lists behind each chart once and
// cache module-wide so every chart shares a single network call. ────────────
let _distStaffCache = null;
let _distStaffPromise = null;
const fetchDistributionStaff = async () => {
  if (_distStaffCache) return _distStaffCache;
  if (!_distStaffPromise) {
    _distStaffPromise = axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/distribution-staff`)
      .then((r) => { _distStaffCache = r.data || { active: [], status: [] }; return _distStaffCache; })
      .catch(() => { _distStaffPromise = null; return { active: [], status: [] }; });
  }
  return _distStaffPromise;
};

// resolve(data, categoryName) → array of member rows for the clicked bar/slice
const useDrilldown = (resolve) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const handleClick = async (entry) => {
    const name = entry?.name ?? entry?.payload?.name ?? entry?.activeLabel;
    if (name === undefined || name === null || name === '') return;
    setTitle(String(name));
    setOpen(true);
    setLoading(true);
    try {
      const data = await fetchDistributionStaff();
      setMembers(resolve(data, name) || []);
    } catch { setMembers([]); }
    finally { setLoading(false); }
  };
  return { open, setOpen, title, members, loading, handleClick };
};

const exportMembersCsv = (label, title, members) => {
  const header = ['#', 'Code', 'Name', 'Designation', 'Department'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [header.map(esc).join(',')];
  members.forEach((m, i) => {
    lines.push([i + 1, m.employee_code, m.name, m.position, m.department].map(esc).join(','));
  });
  const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${label}_${title}`.replace(/[^a-z0-9_-]+/gi, '_') + '.csv';
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const MemberDialog = ({ open, onOpenChange, label, title, members, loading, testid }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid={`drilldown-${testid}`}>
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-2">
          <Users className="h-5 w-5" /> {label}: {title} — {members.length}
          {members.length > 0 && (
            <button
              type="button"
              onClick={() => exportMembersCsv(label, title, members)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition"
              data-testid={`drilldown-${testid}-export`}
            >
              <Download className="h-3.5 w-3.5" /> Export
            </button>
          )}
        </DialogTitle>
      </DialogHeader>
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : members.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No employees found.</div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="text-left p-2.5 font-medium">#</th>
                <th className="text-left p-2.5 font-medium">Code</th>
                <th className="text-left p-2.5 font-medium">Name</th>
                <th className="text-left p-2.5 font-medium">Designation</th>
                <th className="text-left p-2.5 font-medium">Department</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m, idx) => (
                <tr key={m.id || idx} className="border-b hover:bg-gray-50" data-testid={`drilldown-${testid}-row-${idx}`}>
                  <td className="p-2.5 text-gray-500">{idx + 1}</td>
                  <td className="p-2.5 font-mono text-xs">{m.employee_code}</td>
                  <td className="p-2.5 font-medium">{m.name}</td>
                  <td className="p-2.5">{m.position}</td>
                  <td className="p-2.5">{m.department}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const CLICK_HINT = <span className="text-xs font-normal text-gray-400">· click to view names</span>;

// ─── Bar/Pie toggle that lives in each chart's header. Persisted by parent. ──
const ChartToggle = ({ type, onChange, testid }) => {
  if (!onChange) return null;
  const base = 'inline-flex items-center justify-center p-1.5 transition-colors';
  return (
    <div className="inline-flex rounded-md border border-gray-200 overflow-hidden" data-testid={`chart-toggle-${testid}`}>
      <button
        type="button"
        onClick={() => onChange('bar')}
        className={`${base} ${type !== 'pie' ? 'bg-violet-100 text-violet-700' : 'bg-white text-gray-400 hover:bg-gray-50'}`}
        title="Bar chart"
        data-testid={`chart-toggle-${testid}-bar`}
      >
        <BarChart3 className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onChange('pie')}
        className={`${base} border-l border-gray-200 ${type === 'pie' ? 'bg-violet-100 text-violet-700' : 'bg-white text-gray-400 hover:bg-gray-50'}`}
        title="Pie chart"
        data-testid={`chart-toggle-${testid}-pie`}
      >
        <PieChartIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// ─── Generic donut/pie renderer used when chartType === 'pie' ────────────────
const PieView = ({ data, height = 250, colorFn, onSliceClick }) => (
  <ResponsiveContainer width="100%" height={height}>
    <PieChart>
      <Pie
        data={data}
        dataKey="value"
        nameKey="name"
        cx="50%"
        cy="50%"
        outerRadius={Math.min(height / 2 - 30, 95)}
        innerRadius={45}
        paddingAngle={1}
        label={({ value }) => value}
        labelLine={false}
        onClick={onSliceClick ? (entry) => onSliceClick(entry) : undefined}
        style={onSliceClick ? { cursor: 'pointer' } : undefined}
      >
        {data.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={colorFn ? colorFn(entry, index) : COLORS[index % COLORS.length]} />
        ))}
      </Pie>
      <Tooltip
        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
        formatter={(value, _n, p) => [value, p?.payload?.name || 'Count']}
      />
      <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
    </PieChart>
  </ResponsiveContainer>
);

const ChartHeader = ({ title, chartType, onToggleType, testid, extra }) => (
  <CardHeader>
    <CardTitle className="flex items-center gap-3 pr-16">
      <span className="flex items-center gap-2">{title}{extra}</span>
      <ChartToggle type={chartType} onChange={onToggleType} testid={testid} />
    </CardTitle>
  </CardHeader>
);

const NationalityChart = ({ data, chartType = 'bar', onToggleType }) => {
  const chartData = prepareNationalityData(data);
  const dd = useDrilldown((d, name) =>
    (d.active || []).filter((e) => canonicalizeNationality(e.nationality) === name));

  return (
    <Card>
      <ChartHeader title="Nationality Distribution" chartType={chartType} onToggleType={onToggleType} testid="nationality" extra={CLICK_HINT} />
      <CardContent>
        {chartType === 'pie' ? (
          <PieView data={chartData} height={Math.max(280, Math.min(chartData.length * 22, 420))} onSliceClick={dd.handleClick} />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 35)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 40 }} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                formatter={(value) => [value, 'Count']}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} onClick={dd.handleClick} cursor="pointer">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
                <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 'bold', fill: '#374151' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
      <MemberDialog open={dd.open} onOpenChange={dd.setOpen} label="Nationality" title={dd.title} members={dd.members} loading={dd.loading} testid="nationality" />
    </Card>
  );
};

const GenderChart = ({ data, chartType = 'bar', onToggleType }) => {
  const chartData = prepareChartData(data);
  const genderColor = (entry, index) => (index === 0 ? '#3b82f6' : '#ec4899');
  const dd = useDrilldown((d, name) => (d.active || []).filter((e) => (e.gender || '') === name));

  return (
    <Card>
      <ChartHeader title="Gender Distribution" chartType={chartType} onToggleType={onToggleType} testid="gender" extra={CLICK_HINT} />
      <CardContent>
        {chartType === 'pie' ? (
          <PieView data={chartData} colorFn={genderColor} onSliceClick={dd.handleClick} />
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData} margin={{ top: 20, bottom: 20 }} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                formatter={(value) => [value, 'Count']}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} onClick={dd.handleClick} cursor="pointer">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={genderColor(entry, index)} />
                ))}
                <LabelList dataKey="value" position="top" style={{ fontSize: 14, fontWeight: 'bold', fill: '#374151' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
      <MemberDialog open={dd.open} onOpenChange={dd.setOpen} label="Gender" title={dd.title} members={dd.members} loading={dd.loading} testid="gender" />
    </Card>
  );
};

const DepartmentChart = ({ data, chartType = 'bar', onToggleType }) => {
  const chartData = prepareChartData(data).sort((a, b) => b.value - a.value);
  const dd = useDrilldown((d, name) => (d.active || []).filter((e) => (e.department || '') === name));

  return (
    <Card>
      <ChartHeader title="Department Distribution" chartType={chartType} onToggleType={onToggleType} testid="department" extra={CLICK_HINT} />
      <CardContent>
        {chartType === 'pie' ? (
          <PieView data={chartData} height={Math.max(280, Math.min(chartData.length * 24, 420))} onSliceClick={dd.handleClick} />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 40)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 40 }} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                formatter={(value) => [value, 'Count']}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} onClick={dd.handleClick} cursor="pointer">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
                <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 'bold', fill: '#374151' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
      <MemberDialog open={dd.open} onOpenChange={dd.setOpen} label="Department" title={dd.title} members={dd.members} loading={dd.loading} testid="department" />
    </Card>
  );
};

const EmploymentStatusChart = ({ data, chartType = 'bar', onToggleType }) => {
  const chartData = prepareChartData(data);
  const dd = useDrilldown((d, name) =>
    (d.status || []).filter((e) => (e.employment_status || 'Active') === name));

  const getStatusColor = (entry) => {
    const name = entry?.name;
    if (name === 'Terminated' || name === 'Resigned') return '#ef4444';
    if (name === 'Suspended' || name === 'On leave') return '#f97316';
    return '#10b981';
  };

  return (
    <Card>
      <ChartHeader title="Employment Status" chartType={chartType} onToggleType={onToggleType} testid="employment-status" extra={CLICK_HINT} />
      <CardContent>
        {chartType === 'pie' ? (
          <PieView data={chartData} colorFn={getStatusColor} onSliceClick={dd.handleClick} />
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData} margin={{ top: 30, bottom: 20 }} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                formatter={(value) => [value, 'Count']}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} onClick={dd.handleClick} cursor="pointer">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getStatusColor(entry)} />
                ))}
                <LabelList dataKey="value" position="top" style={{ fontSize: 14, fontWeight: 'bold', fill: '#374151' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
      <MemberDialog open={dd.open} onOpenChange={dd.setOpen} label="Status" title={dd.title} members={dd.members} loading={dd.loading} testid="employment-status" />
    </Card>
  );
};

const API = process.env.REACT_APP_BACKEND_URL;

const VisaCompanyChart = ({ data, holders = {}, chartType = 'bar', onToggleType }) => {
  // Merge active staff with non-employee visa holders so the count reflects both.
  const names = [...new Set([...Object.keys(data || {}), ...Object.keys(holders || {})])];
  const chartData = names.map(name => {
    const staff = (data || {})[name] || 0;
    const h = (holders || {})[name] || 0;
    return { name, staff, holders: h, value: staff + h };
  }).sort((a, b) => b.value - a.value);
  const totalHolders = Object.values(holders || {}).reduce((a, b) => a + b, 0);
  const [staffDialog, setStaffDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleBarClick = async (entry) => {
    const company = entry?.name || entry?.payload?.name || entry?.activeLabel;
    if (!company) return;
    setSelectedCompany(company);
    setStaffDialog(true);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/dashboard/visa-company-staff`);
      setStaffList(res.data[company] || []);
    } catch { setStaffList([]); }
    finally { setLoading(false); }
  };

  const getExpiryBadge = (expiryDate) => {
    if (!expiryDate) return <Badge variant="outline" className="text-xs text-gray-400">N/A</Badge>;
    const exp = new Date(expiryDate);
    const today = new Date();
    const daysLeft = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return <Badge className="bg-red-500 text-xs">Expired {Math.abs(daysLeft)}d ago</Badge>;
    if (daysLeft <= 30) return <Badge className="bg-red-500 text-xs">{daysLeft}d left</Badge>;
    if (daysLeft <= 60) return <Badge className="bg-yellow-500 text-xs">{daysLeft}d left</Badge>;
    return <Badge className="bg-green-500 text-xs">{exp.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</Badge>;
  };

  return (
    <>
      <Card>
        <ChartHeader
          title="Visa Company Distribution"
          chartType={chartType}
          onToggleType={onToggleType}
          testid="visa-company"
          extra={<span className="text-xs font-normal text-gray-500">· staff + visa holders{totalHolders > 0 ? ` (+${totalHolders})` : ''} · click to view staff</span>}
        />
        <CardContent>
          {chartType === 'pie' ? (
            <PieView data={chartData} onSliceClick={handleBarClick} />
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData} margin={{ top: 30, bottom: 20 }} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, _n, props) => {
                    const p = props?.payload || {};
                    return [`${p.staff || 0} staff${p.holders ? ` + ${p.holders} holder${p.holders > 1 ? 's' : ''}` : ''} = ${p.value}`, 'Visa count'];
                  }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} onClick={(entry) => handleBarClick(entry)} cursor="pointer">
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                  <LabelList dataKey="value" position="top" content={(props) => {
                    const { x, y, width, index } = props;
                    const d = chartData[index];
                    if (!d) return null;
                    const label = d.holders > 0 ? `${d.staff} + ${d.holders}` : `${d.value}`;
                    return <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={13} fontWeight="bold" fill="#374151">{label}</text>;
                  }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={staffDialog} onOpenChange={setStaffDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Staff under "{selectedCompany}" — {staffList.length} employees
            </DialogTitle>
          </DialogHeader>
          {loading ? <div className="text-center py-8 text-gray-500">Loading...</div> : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="text-left p-2.5 font-medium">#</th>
                    <th className="text-left p-2.5 font-medium">Code</th>
                    <th className="text-left p-2.5 font-medium">Name</th>
                    <th className="text-left p-2.5 font-medium">Position</th>
                    <th className="text-left p-2.5 font-medium">Department</th>
                    <th className="text-center p-2.5 font-medium">Visa Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {staffList.map((emp, idx) => (
                    <tr key={emp.id} className="border-b hover:bg-gray-50">
                      <td className="p-2.5 text-gray-500">{idx + 1}</td>
                      <td className="p-2.5 font-mono text-xs">{emp.employee_code}</td>
                      <td className="p-2.5 font-medium">{emp.name}</td>
                      <td className="p-2.5">{emp.position}</td>
                      <td className="p-2.5">{emp.department}</td>
                      <td className="p-2.5 text-center">{getExpiryBadge(emp.visa_expiry)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export { NationalityChart, GenderChart, DepartmentChart, EmploymentStatusChart, VisaCompanyChart };
